/**
 * Profile ⇄ run glue: compile the party from the profile before a key, fold the result back in
 * after it. Pure functions (no DOM, no storage) so they are unit-testable.
 *
 * Before:  prepareRun(profile, baseRegistry, { spectate })  → registry + party for startSession
 * After:   finishRun(profile, { result, party, botPreset, seed, kills }) → new profile + summary
 */
import type { BotSkillPreset, ClassId, ContentRegistry, PartyMemberConfig, RunResult } from '@keystone/sim';
import {
  type AutopilotReport,
  type CompiledCharacter,
  type KillReward,
  type Profile,
  type ResourceBundle,
  type RunInput,
  type RunOutcome,
  type RunPayout,
  type RitualChoice,
  type VaultOptionKind,
  RANK_TIER_ORDER,
  autopilot,
  claimVault,
  claimWeeklyRitual,
  equippedGear,
  profileStanding,
  setPieces,
  vaultOptionBundle,
  weeklyReady,
  buildPartyRegistry,
  compileCompanion,
  compileProfileCharacter,
  groupFinderParty,
  heroRole,
  heroSpecId,
  partyClassDef,
  itemOwner,
  applyRunResult,
  profileRng,
  worldFindsToBundle,
} from '@keystone/progression';
import { PARTY_ORDER, heroFor } from './heroes';

// ---------------------------------------------------------------------------
// Party / main class
// ---------------------------------------------------------------------------

/**
 * Party classes in slot order (tank, healer, dps, dps): the player's class plus the other three
 * (Group Finder fills them, docs/design/zones.md). One slot per class (the registry holds one
 * ClassDef per class id).
 */
export function partyClasses(profile: Profile): ClassId[] {
  const main = profile.character?.classId ?? profile.mainClassId;
  // +specs: a spec'd hero queues by its spec's role (a Ravager Warrior is dps; the tank is a stranger).
  if (heroSpecId(profile)) return groupFinderParty(main, heroRole(profile), heroLevel(profile), profile.runs.length + 1).map((s) => s.classId);
  const order = PARTY_ORDER.includes(main) ? [...PARTY_ORDER] : [...PARTY_ORDER.slice(0, 3), main];
  return order.slice(0, 4);
}

/** The hero's level (1 before character creation). */
export function heroLevel(profile: Profile): number {
  const c = profile.character?.classId ?? profile.mainClassId;
  return profile.characters[c]?.hero.level ?? 1;
}

/**
 * Make `classId` the human's class. The new main stops being auto-managed (its gear/talents are
 * only changed by the player); the old main becomes a companion and is auto-managed again.
 * (Adapter: progression has no "switch main class" op.)
 */
export function setMainClass(profile: Profile, classId: ClassId): Profile {
  if (profile.mainClassId === classId || !profile.characters[classId]) return profile;
  const p = structuredClone(profile);
  const old = p.characters[p.mainClassId];
  if (old) old.autoManaged = true;
  p.characters[classId]!.autoManaged = false;
  p.mainClassId = classId;
  return p;
}

// ---------------------------------------------------------------------------
// Keys
// ---------------------------------------------------------------------------

export const MIN_KEY = 2;
export const MAX_KEY = 30;

/** Highest timed key level on any dungeon (0 when none). */
export function highestTimedKey(profile: Profile): number {
  let best = 0;
  for (const rec of Object.values(profile.rating)) best = Math.max(best, rec.best?.level ?? 0);
  for (const r of profile.runs) if (r.timed) best = Math.max(best, r.level);
  return best;
}

/**
 * Highest key level the player may start: one above their best timed key (keystone upgrade),
 * never below +2. Any lower level is always allowed.
 */
export function maxUnlockedKey(profile: Profile): number {
  return Math.max(MIN_KEY, Math.min(MAX_KEY, highestTimedKey(profile) + 1));
}

export function clampKey(profile: Profile, level: number): number {
  return Math.max(MIN_KEY, Math.min(maxUnlockedKey(profile), Math.round(level) || MIN_KEY));
}

/** Deterministic per-run sim seed from the profile seed and run index. */
export function runSeed(profile: Profile): number {
  return ((profile.seed ^ Math.imul(profile.runs.length + 1, 0x9e3779b1)) >>> 0) % 1_000_000_000;
}

// ---------------------------------------------------------------------------
// Before a run
// ---------------------------------------------------------------------------

export interface RunSetup {
  /** Compiled registry for createSim + bots (the base registry if compiling failed). */
  registry: ContentRegistry;
  /** Party in slot order, named after the heroes. */
  party: PartyMemberConfig[];
  /** Class ids in slot order (Session `party`). */
  classIds: ClassId[];
  /**
   * +field: level per slot (the hero's level; Group Finder companions match it). The session sets
   * PartyMemberConfig.level from it for levelled content; `party` carries it already when
   * prepareRun was asked for a levelled run.
   */
  levels: number[];
  /** Hero names in slot order (Session `partyNames`). */
  names: string[];
  /** The human's slot, or -1 when spectating. */
  slot: number;
  compiled: CompiledCharacter[];
  /** Set when compile failed and the run uses uncompiled classes. */
  compileError: string | null;
  /**
   * +world-resources: SimConfig.looterId: the unit that breaks clutter and collects pickups.
   * The human's unit; when spectating (autopilot) the main class's bot loots for the player.
   */
  looterId: string;
}

/**
 * Compile the party and build the registry the sim and bots run on. The player's character is
 * compiled from the profile (gear, gems, talents); the other slots are Group Finder companions
 * compiled from starter gear at the hero's level (never stored). `base` must be the UNcompiled
 * content registry. `levelled`: also put the levels on the party configs (fields, the levelled
 * starter dungeon); M+ keys stay unlevelled in Phase 1.
 */
export function prepareRun(profile: Profile, base: ContentRegistry, opts: { spectate?: boolean; levelled?: boolean } = {}): RunSetup {
  const classIds = partyClasses(profile);
  const main = profile.character?.classId ?? profile.mainClassId;
  const slot = opts.spectate ? -1 : classIds.indexOf(main);
  const level = heroLevel(profile);
  const levels = classIds.map(() => level);
  const names = classIds.map((c, i) => (i === classIds.indexOf(main) && profile.character ? profile.character.name : heroFor(c).name));
  const party: PartyMemberConfig[] = classIds.map((classId, i) => ({ classId, name: names[i]!, controller: i === slot ? 'human' : 'bot', ...(opts.levelled ? { level: levels[i]! } : {}) }));
  let registry = base;
  let compiled: CompiledCharacter[] = [];
  let compileError: string | null = null;
  try {
    compiled = classIds.map((c) => {
      const def = base.bundle.classes.find((k) => k.id === c) ?? partyClassDef(c); // +specs: spec-keyed strangers
      if (!def) throw new Error(`class ${c} is not in the content bundle`);
      // The player's own hero (stored); everyone else is a Group Finder companion (compiled, not stored).
      return profile.characters[c] && c === main ? compileProfileCharacter(profile, def) : compileCompanion(def, level);
    });
    registry = buildPartyRegistry(base.bundle, compiled);
  } catch (err) {
    compileError = err instanceof Error ? err.message : String(err);
    compiled = [];
    registry = base;
  }
  const lootSlot = slot >= 0 ? slot : Math.max(0, classIds.indexOf(main));
  return { registry, party, classIds, levels, names, slot, compiled, compileError, looterId: `p${lootSlot + 1}` };
}

// ---------------------------------------------------------------------------
// After a run
// ---------------------------------------------------------------------------

/** Share of a run's gold the bot autopilot may spend on companions (their forging itself is free). */
export const BOT_SPEND_SHARE = 0.5;

/**
 * Autopilot for the bot companions only. Companions forge from their own satchels (paid every
 * run) at base odds and for no gold (crafting.md §11); progression's `autopilot` would also
 * claim the vault (the player's pick), equip / salvage from the shared bag (the player's crafts)
 * and use the gem stash, so this adapter hides those from it:
 *  - the vault, unequipped bag items and the gem stash are set aside and restored;
 *  - gold is capped at `budget.gold`; only what it spends is deducted;
 *  - `classes` never includes the human-managed main class; Venom / Eyes / Blessed are never spent;
 *  - the pieces companions replace are salvaged (their small yield goes to the wallet), so the
 *    player's bag only ever holds the player's own crafts.
 * Spare Altar Sigils go to companions only when the player's sharing setting allows it.
 */
export function botAutopilot(profile: Profile, classes: readonly ClassId[], budget: { gold: number }): AutopilotReport | null {
  const bots = classes.filter((c) => c !== profile.mainClassId && profile.characters[c]?.autoManaged);
  if (!bots.length) return null;
  const bag = new Set(profile.inventory.filter((i) => !itemOwner(profile, i.id)).map((i) => i.id));
  const gold = Math.max(0, Math.min(profile.currencies.gold, Math.floor(budget.gold)));
  const sandbox: Profile = {
    ...profile,
    inventory: profile.inventory.filter((i) => !bag.has(i.id)),
    gems: {},
    vault: { ...profile.vault, pending: null, offer: null },
    currencies: { ...profile.currencies, gold },
  };
  const rep = autopilot(sandbox, { classes: bots, scrap: true, vault: false, reagents: false });
  const out = rep.profile;
  const spentGold = gold - out.currencies.gold;
  const bagItems = profile.inventory.filter((i) => bag.has(i.id));
  const result: Profile = {
    ...out,
    inventory: [...out.inventory, ...bagItems],
    gems: profile.gems,
    vault: profile.vault,
    currencies: { ...out.currencies, gold: profile.currencies.gold - spentGold },
  };
  return { ...rep, profile: result };
}

export interface RunSummary {
  outcome: RunOutcome;
  /** Bot autopilot pass (null when no bots were managed). */
  bots: AutopilotReport | null;
  /** The profile after the run + bot autopilot (what to save). */
  profile: Profile;
  mainClassId: ClassId;
  xp: number;
  gold: number;
  /** The Altar Offering: every resource line this run paid (the end screen's reveal). */
  payout: RunPayout;
  /** The run as played (key, timed, chest) for the Offering's lines. */
  run: { level: number; timed: boolean; completed: boolean; chest: 0 | 1 | 2 | 3; dungeonId: string };
  /** The main character's level before/after. */
  level: { before: number; after: number; xpAfter: number };
  ratingDelta: number;
  tierChanged: boolean;
}

export interface FinishRunInput {
  result: RunResult;
  /** Slot order, as the run was played. */
  party: { classId: ClassId; controller: 'human' | 'bot' }[];
  botPreset: BotSkillPreset | null;
  seed: number;
  kills?: KillReward;
  /** The player's local date 'YYYY-MM-DD' (Daily Offering, Weekly Ritual). Omitted = no daily logic. */
  localDate?: string;
  /** World finds picked up during the run (breakables, caches, eggs), if the session reports them. */
  world?: ResourceBundle;
  bossesKilled?: number;
}

/**
 * applyRunResult (rating, the Altar Offering, XP, gold, vault, companions' satchels) with the
 * profile's deterministic RNG and the caller's local date, then the bot-only autopilot. The
 * human's gear and talents are never changed here.
 */
export function finishRun(profile: Profile, input: FinishRunInput): RunSummary {
  const runIndex = profile.runs.length + 1;
  const run: RunInput = { result: input.result, party: input.party, botPreset: input.botPreset, seed: input.seed, kills: input.kills };
  // +world-resources: explicit values win; otherwise the sim's result carries both (boss kills and
  // the looter's world finds), so every caller pays them without extra plumbing.
  if (input.world) run.world = input.world;
  else if (input.result.worldFinds) run.world = worldFindsToBundle(input.result.worldFinds, input.result.level);
  const bosses = input.bossesKilled ?? input.result.bossesKilled;
  if (bosses !== undefined) run.bossesKilled = bosses;
  const outcome = applyRunResult(profile, run, profileRng(profile, runIndex), input.localDate ? { localDate: input.localDate } : {});
  const bots = botAutopilot(outcome.profile, input.party.map((m) => m.classId), { gold: outcome.rewards.gold * BOT_SPEND_SHARE });
  const after = bots?.profile ?? outcome.profile;
  const main = after.mainClassId;
  const before = profile.characters[main]?.hero.level ?? 1;
  const hero = after.characters[main]?.hero;
  const r = input.result;
  return {
    outcome,
    bots,
    profile: after,
    mainClassId: main,
    xp: outcome.rewards.xp,
    gold: outcome.rewards.gold,
    payout: outcome.payout,
    run: { level: r.level, timed: r.timed, completed: r.completed, chest: r.chest, dungeonId: r.dungeonId },
    level: { before, after: hero?.level ?? before, xpAfter: hero?.xp ?? 0 },
    ratingDelta: outcome.ratingAfter - outcome.ratingBefore,
    tierChanged: outcome.standingAfter.tier !== outcome.standingBefore.tier || outcome.standingAfter.subRank !== outcome.standingBefore.subRank,
  };
}


// ---------------------------------------------------------------------------
// Automatic claims (the simplified UI makes these choices for the player)
// ---------------------------------------------------------------------------

export interface AutoClaims {
  profile: Profile;
  /** Vault cache claimed (null when none was waiting). */
  vault: { kind: VaultOptionKind; bundle: ResourceBundle } | null;
  /** Weekly Ritual cache claimed (null when not ready). */
  weekly: { choice: RitualChoice; bundle: ResourceBundle } | null;
}

/**
 * Claim a waiting vault and a ready Weekly Ritual for the player (web-side, after `finishRun`;
 * the harness and autopilot never call this). Vault: the Altar Cache while the main hero's tier
 * set has fewer than 4 pieces (it carries a Sigil), otherwise the Scale Cache (no tokens minted).
 * Ritual: a Sigil, or a full Serpent's Eye once the set is 5/5 and the player is Champion+.
 * Pure; a claim that fails leaves the profile as it was.
 */
export function autoClaimAfterRun(profile: Profile, localDate: string): AutoClaims {
  let p = profile;
  const pieces = setPieces(equippedGear(p, p.mainClassId), p.mainClassId);
  let vault: AutoClaims['vault'] = null;
  const offer = p.vault.offer;
  if (offer?.options.length) {
    const want: VaultOptionKind = pieces < 4 ? 'altar' : 'scales';
    let i = offer.options.findIndex((o) => o.kind === want);
    if (i < 0) i = offer.options.findIndex((o) => o.kind !== 'token');
    if (i < 0) i = 0;
    const opt = offer.options[i]!;
    try {
      p = claimVault(p, i);
      vault = { kind: opt.kind, bundle: vaultOptionBundle(opt) };
    } catch {
      /* leave it for later */
    }
  } else if (p.vault.pending?.options.length) {
    try {
      p = claimVault(p, 0);
    } catch {
      /* legacy item vault: leave it */
    }
  }
  let weekly: AutoClaims['weekly'] = null;
  if (weeklyReady(p.forge.weekly, localDate)) {
    const champion = RANK_TIER_ORDER.indexOf(profileStanding(p).tier) >= RANK_TIER_ORDER.indexOf('champion');
    const choice: RitualChoice = pieces >= 5 && champion ? 'eye' : 'sigil';
    const r = claimWeeklyRitual(p, choice, localDate);
    if (r.ok) {
      p = r.profile;
      weekly = { choice, bundle: r.gained ?? {} };
    }
  }
  return { profile: p, vault, weekly };
}
